import { Avatar, Badge, Button } from '@parsina/framework';
import { C, soft } from '@parsina/framework/tokens';
import { LTR_TEXT, useTranslation } from '../../i18n';
import MobileCard, { MobileCardHead, MobileCardMeta, MobileCardActions } from '../MobileCard';
import MobileRowMenu from '../MobileRowMenu';

/**
 * One appointment, as a card (docs/MOBILE-REDESIGN.md §4.7, §6.1).
 *
 * **Render-only, and deliberately vocabulary-free.** Every word and every status
 * colour arrives already resolved: `flow`, `pay`, `urgency` and `meta` come from
 * the reception vocabulary (`Reception/dayPanel.jsx`) and `ops` from the shared
 * op matrix (`Reception/rowOps.js`). That is what keeps `src/mobile/` a LAYOUT
 * library — a card that imported a screen's vocabulary would make the dependency
 * point the wrong way, and a card that re-derived «حاضر» would be the second
 * answer §2.5 forbids.
 *
 * The anatomy is fixed because a desk scans forty of these:
 *
 *   ① queue position · time · urgency ……………………………… flow status
 *   ② avatar · name · «بدون پرونده» …………………… pay chip + amount
 *   ③ پرونده {n} · حضوری · {phone}
 *   ③b a finished visit's «کد رهگیری نسخه» chips, when it has any (PM-0686)
 *   ④ the primary action (+ a companion, when there is one) …………… ⋮
 *
 * ④ is the first `primary` op from the matrix; every other operation is a ≥44px
 * row in the sheet behind ⋮. A phone that laid four small buttons across a card
 * would give each of them about 70px, and «لغو نوبت» beside «ثبت حضور» at 70px
 * is a cancelled appointment.
 *
 * AT MOST ONE op may join it, and only by asking: `companion` on the matrix. It
 * splits the shelf in half — never a third button, which is how the 70px row
 * starts. Today that is «شروع ویزیت» / «ادامهٔ ویزیت» on the row of a person who
 * is both this desk and this doctor: the money is the desk's job and the visit is
 * theirs, and neither may be buried in a menu behind the other. It is rendered
 * `teal` against the primary's navy — the clinical act and the desk's act are not
 * the same kind of act, and at a glance the colour is what says so.
 *
 * The whole card body is the tap target for the patient's record — the same
 * thing the desktop row click does. It is only interactive when there IS a
 * record: a «ثبت سریع» placeholder has no file to open, so the card is inert
 * and «تکمیل پرونده» is its primary action instead.
 */

// The pay chip's four states, in the kit's own semantic variants rather than a
// fifth palette invented here.
const PAY_VARIANT = {
  settled: 'success',
  partial: 'warning',
  due: 'destructive',
  free: 'outline',
};

// «پیش‌پرداخت» (PM-0657 §6.3): held is the teal of money the clinic holds, a
// missing REQUIRED deposit the amber of a warning, the closed states neutral.
const DEPOSIT_VARIANT = {
  held: 'success',
  missing: 'warning',
};

export default function MobileAppointmentCard({
  /** The derived reception row: `{ id, name, time, placeholder, … }`. */
  row,
  /** Position in this doctor's queue, or null when the patient is not in it. */
  queuePos = null,
  /** `{ label, dot }` from FLOW_STATUS — where the patient is in the visit flow. */
  flow,
  /** `{ label, cls }` from PAY_STATUS, or null. */
  pay = null,
  /** The outstanding amount, ALREADY in toman and formatted. Null when nothing is due. */
  amount = null,
  /** `{ l, color, bg }` from URGENCY_BADGE, or null. */
  urgency = null,
  /** The sub-line's parts, already built and already digit-formatted. */
  meta = [],
  /** The ordered op list from `rowOperations()`. */
  ops = [],
  /** Opens the patient's record. Omit for a row with no patient. */
  onOpen,
  /** «نفر بعدی» / «نوبت {n}», already worded and digit-formatted. */
  queueLabel = null,
  /** The time, already digit-formatted. */
  timeLabel = null,
  /** «بدون پرونده» — the marker on a «ثبت سریع» row. */
  noFileLabel = null,
  /** «آنلاین» — the patient booked it themselves (PM-0382), already worded. */
  onlineLabel = null,
  /** Which online channel it was, as a sentence for the tooltip. */
  onlineHint = null,
  /** Dims a finished row (ویزیت‌شده / عدم‌حضور / لغو شده). */
  muted = false,
  /** `{ kind, label }` — the «پیش‌پرداخت» chip (PM-0657), already worded; null = none. */
  deposit = null,
  /**
   * The finished visit's «کد رهگیری نسخه» chips (PM-0686), already a node — the card
   * only gives it a place, so it stays vocabulary-free. Null = nothing to show.
   */
  erx = null,
  'data-testid': testId,
}) {
  const { t } = useTranslation();
  const primary = ops.find(o => o.primary) || ops[0] || null;
  // Only when it is not already the primary — a paid row has no money op, so the
  // visit IS `ops[0]` there and takes the whole shelf, as it did before.
  const companion = ops.find(o => o.companion && o !== primary) || null;
  const rest = ops.filter(o => o !== primary && o !== companion);
  const emergency = row?.urgency === 'emergency';

  return (
    <MobileCard
      onClick={onOpen}
      // The overlay that makes the whole card tappable has no text of its own,
      // so it is named here. Without it a screen reader announces the card's
      // primary affordance as an unlabelled button.
      label={row?.name ? t('reception.mobile.openRecordFor', { name: row.name }) : undefined}
      navigates={Boolean(onOpen)}
      marker={flow?.dot || null}
      muted={muted}
      // An emergency is a safety fact (§2.4 rule 1): it stays visible at a
      // glance even when the row is finished and dimmed.
      tone={emergency ? soft(C.red, 0.06) : null}
      data-testid={testId}
      data-state={row?.flow}
    >
      {/* ① */}
      <MobileCardHead>
        {queueLabel ? (
          <Badge variant={queuePos === 1 ? 'default' : 'outline'} data-testid={testId ? `${testId}-queue` : undefined}>
            {queueLabel}
          </Badge>
        ) : null}

        {timeLabel ? (
          <span
            {...LTR_TEXT}
            style={{
              fontSize: '0.9375rem', fontWeight: 800,
              fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em',
              color: C.textPrimary,
            }}
          >
            {timeLabel}
          </span>
        ) : null}

        {urgency ? (
          <span style={{
            fontSize: '0.625rem', fontWeight: 800, borderRadius: '0.3125rem',
            padding: '0.0625rem 0.375rem', whiteSpace: 'nowrap', flexShrink: 0,
            color: urgency.color, background: urgency.bg,
          }}>
            {urgency.l}
          </span>
        ) : null}

        <span style={{ flex: 1 }} />

        {flow ? (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.3125rem',
            fontSize: '0.75rem', fontWeight: 700, color: flow.dot, whiteSpace: 'nowrap',
          }}>
            <span aria-hidden="true" style={{
              width: '0.375rem', height: '0.375rem', borderRadius: '50%',
              background: flow.dot, display: 'inline-block',
            }} />
            {flow.label}
          </span>
        ) : null}
      </MobileCardHead>

      {/* ② */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.5rem',
        marginTop: '0.5rem', minWidth: 0,
      }}>
        <Avatar name={row?.name} size={34} />
        <span
          dir="auto"
          style={{
            flex: 1, minWidth: 0, fontSize: '0.875rem', fontWeight: 700,
            color: C.textPrimary,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}
        >
          {row?.name}
        </span>

        {noFileLabel ? <Badge variant="warning">{noFileLabel}</Badge> : null}

        {/* The panel's info tint, never red: a fact about the row, not a problem. */}
        {onlineLabel ? (
          <Badge
            variant="default"
            title={onlineHint || undefined}
            data-testid={testId ? `${testId}-online` : undefined}
          >
            {onlineLabel}
          </Badge>
        ) : null}

        {pay ? (
          <Badge variant={PAY_VARIANT[pay.cls] || 'outline'} data-testid={testId ? `${testId}-pay` : undefined}>
            {pay.label}
            {amount ? <b {...LTR_TEXT} style={{ marginInlineStart: '0.25rem', fontWeight: 800 }}>{amount}</b> : null}
          </Badge>
        ) : null}

        {deposit ? (
          <Badge
            variant={DEPOSIT_VARIANT[deposit.kind] || 'outline'}
            data-testid={testId ? `${testId}-deposit` : 'reception-deposit-chip'}
            data-kind={deposit.kind}
          >
            {deposit.label}
          </Badge>
        ) : null}
      </div>

      {/* ③ */}
      <MobileCardMeta parts={meta} />

      {/* ③b — the chips are buttons (a copy), so they sit above the card's whole-body
          tap overlay exactly as the action shelf does (see `MobileCardActions`). */}
      {erx ? (
        <div style={{ position: 'relative', zIndex: 2, marginTop: '0.5rem' }}>
          {erx}
        </div>
      ) : null}

      {/* ④ */}
      {primary ? (
        <MobileCardActions>
          <Button
            variant={primary.danger ? 'destructive' : 'default'}
            // The card itself navigates; an action inside it must not do both.
            onClick={primary.onClick}
            // `flex: 1` on both halves, and `minWidth: 0` so a long label
            // ellipsises inside its half instead of pushing the other one out.
            style={{ flex: 1, minWidth: 0, minHeight: '2.75rem' }}
            data-testid={testId ? `${testId}-primary` : undefined}
          >
            {primary.label}
          </Button>

          {companion ? (
            <Button
              variant="teal"
              onClick={companion.onClick}
              style={{ flex: 1, minWidth: 0, minHeight: '2.75rem' }}
              data-testid={testId ? `${testId}-companion` : undefined}
            >
              {companion.label}
            </Button>
          ) : null}

          {rest.length > 0 && (
            <MobileRowMenu
              subject={row?.name}
              items={rest.map(o => ({
                id: o.id,
                label: o.label,
                tone: o.danger ? 'danger' : undefined,
                // One rule between the ops that act on the VISIT and the ops that
                // act on the PATIENT — «لغو نوبت» and «پرونده بیمار» flush against
                // each other invite the mis-click.
                separatorBefore: o.patient && rest.indexOf(o) > 0 && !rest[rest.indexOf(o) - 1].patient,
                onClick: o.onClick,
              }))}
              data-testid={testId ? `${testId}-menu` : undefined}
            />
          )}
        </MobileCardActions>
      ) : null}
    </MobileCard>
  );
}
